import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import {
  FORGE_DESIGNS_DASHBOARD_PATH,
  FORGE_DESIGN_GROUPS_DASHBOARD_PATH,
  FORGE_MASTER_LABEL_PATH,
  FORGE_PR_INFERENCE_OKRS_PATH,
  FORGE_REMOTE_DC_CONFIG_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_STORE_DC_TASK_LIST_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
  FORGE_UNRESOLVED_TASK_LIST_PATH,
  FORGE_VIZSIM_DATASETS_DASHBOARD_PATH,
} from '../../paths';
import { MasterLabelLink } from '../common/MasterLabelLink';
import { ForgeAppSwitcher, ForgeEnvironmentSwitcher } from './ForgeEnvironmentSwitcher';

interface ForgeSidebarProps {
  theme: 'dark' | 'light';
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  isMobile?: boolean;
  toggleCollapse: () => void;
  hideSidebar: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  onMobileNavigate?: () => void;
}

interface NavSection {
  title: string;
  items: { label: string; icon: React.ReactNode; path: string }[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Dashboards',
    items: [
      {
        label: 'System Health',
        icon: <MonitorHeartOutlinedIcon />,
        path: FORGE_SYSTEM_HEALTH_PATH,
      },
      {
        label: 'PR Inference OKRs',
        icon: <QueryStatsOutlinedIcon />,
        path: FORGE_PR_INFERENCE_OKRS_PATH,
      },
      {
        label: 'Designs',
        icon: <StyleOutlinedIcon />,
        path: FORGE_DESIGNS_DASHBOARD_PATH,
      },
      {
        label: 'Design Groups',
        icon: <AccountTreeOutlinedIcon />,
        path: FORGE_DESIGN_GROUPS_DASHBOARD_PATH,
      },
      {
        label: 'VizSim Datasets',
        icon: <DatasetOutlinedIcon />,
        path: FORGE_VIZSIM_DATASETS_DASHBOARD_PATH,
      },
    ],
  },
  {
    title: 'Task Lists',
    items: [
      {
        label: 'Store DC',
        icon: <ImageSearchOutlinedIcon />,
        path: FORGE_STORE_DC_TASK_LIST_PATH,
      },
      {
        label: 'Remote DC',
        icon: <TravelExploreOutlinedIcon />,
        path: FORGE_REMOTE_DC_TASK_LIST_PATH,
      },
      {
        label: 'Unresolved',
        icon: <WarningAmberOutlinedIcon />,
        path: FORGE_UNRESOLVED_TASK_LIST_PATH,
      },
    ],
  },
  {
    title: 'Labeling Tools',
    items: [
      {
        label: 'Remote DC',
        icon: <TravelExploreOutlinedIcon />,
        path: FORGE_REMOTE_DC_TOOL_PATH,
      },
      {
        label: 'Master Label',
        icon: <ViewColumnOutlinedIcon />,
        path: FORGE_MASTER_LABEL_PATH,
      },
    ],
  },
  {
    title: 'Configuration',
    items: [
      {
        label: 'Remote DC',
        icon: <TuneOutlinedIcon />,
        path: FORGE_REMOTE_DC_CONFIG_PATH,
      },
    ],
  },
];

export const ForgeSidebar: React.FC<ForgeSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  isMobile = false,
  toggleCollapse,
  hideSidebar,
  onDragStart,
  toggleTheme,
  currentPath,
  onMobileNavigate,
}) => {
  const { logout, canUseNonAdminPreview, enterNonAdminPreview } = useAuthContext();
  const navigate = useNavigate();
  const effectiveCollapsed = isCollapsed && !isMobile;

  const isActive = (path: string) => {
    if (path === FORGE_MASTER_LABEL_PATH) return currentPath === path;
    return currentPath.startsWith(path);
  };

  const sidebarClasses = [
    'forge-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Forge navigation"
    >
      {/* Nav */}
      <nav className="sidebar-nav">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} className="sidebar-section">
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => {
              const SidebarLink = item.path === FORGE_MASTER_LABEL_PATH ? MasterLabelLink : Link;
              return (
                <SidebarLink
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                  title={effectiveCollapsed ? item.label : undefined}
                  onClick={onMobileNavigate}
                >
                  {item.icon}
                  <span className="sidebar-item-label">{item.label}</span>
                </SidebarLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          {canUseNonAdminPreview ? (
            <button
              className="sidebar-footer-link"
              title={effectiveCollapsed ? 'Labeler preview' : undefined}
              onClick={() => {
                onMobileNavigate?.();
                enterNonAdminPreview();
              }}
            >
              <VisibilityOutlinedIcon />
              <span className="sidebar-collapse-label">Labeler preview</span>
            </button>
          ) : (
            <button
              className="sidebar-footer-link"
              title={effectiveCollapsed ? 'Admin' : undefined}
            >
              <AdminPanelSettingsOutlinedIcon />
              <span className="sidebar-collapse-label">Admin</span>
            </button>
          )}

          <ForgeEnvironmentSwitcher />
          <ForgeAppSwitcher />

          <button
            className="sidebar-footer-link"
            onClick={toggleTheme}
            title={
              effectiveCollapsed
                ? `Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`
                : undefined
            }
          >
            {theme === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            <span className="sidebar-collapse-label">
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              logout();
              navigate('/stores');
            }}
            title={effectiveCollapsed ? 'Sign out' : undefined}
          >
            <LogoutIcon />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>

        {!isMobile ? (
          <>
            <div className="sidebar-footer-divider" />

            <button className="sidebar-collapse-btn" onClick={hideSidebar} title="Hide sidebar">
              <ChevronLeftIcon />
              <span className="sidebar-collapse-label">Hide</span>
            </button>
          </>
        ) : null}
      </div>

      {!isMobile ? <div className="sidebar-drag-handle" onMouseDown={onDragStart} /> : null}
    </aside>
  );
};
